import { BRAND_ASSETS } from '../config/assets';

export const SITE_ORIGIN = 'https://agrollama.com';

// Варианты написания бренда (кириллица/латиница, с пробелом и без) — помогают
// поисковикам связать запросы «агролама», «агроллама», «agrolama» и т.п. с AgroLlama
// и не «исправлять» их на другие слова. Основное имя — 'AgroLlama' (поле name).
const BRAND_ALTERNATE_NAMES = [
  'Агролама',
  'Агроллама',
  'Агро Лама',
  'Агро Ллама',
  'Agrolama',
  'Agrollama',
  'Agro Llama',
  'agrollama.com',
];

// Официальные профили бренда — укрепляют «сущность» бренда в Knowledge Graph Google.
const BRAND_SAME_AS = [
  'https://play.google.com/store/apps/details?id=com.agrollama',
  'https://apps.apple.com/app/id6802196676',
];

export const LANGUAGES = {
  en: {
    htmlLang: 'en',
    hreflang: 'en',
    prefix: '',
    ogLocale: 'en_US',
  },
  ru: {
    htmlLang: 'ru',
    hreflang: 'ru',
    prefix: '/ru',
    ogLocale: 'ru_RU',
  },
  kz: {
    htmlLang: 'kk',
    hreflang: 'kk-KZ',
    prefix: '/kz',
    ogLocale: 'kk_KZ',
  },
};

export const PAGE_PATHS = {
  home: '',
  privacy: '/privacy',
  terms: '/terms',
  support: '/support',
  deletion: '/account-deletion',
};

const SEO_COPY = {
  ru: {
    home: {
      title: 'AgroLlama — агромаркетплейс для фермеров и закупщиков',
      description:
        'AgroLlama — агромаркетплейс для фермеров и закупщиков: B2B-сделки, покупка и продажа сельхозпродукции, зерна, техники и удобрений с ИИ-ассистентами.',
    },
    privacy: {
      title: 'Политика конфиденциальности — AgroLlama',
      description:
        'Политика конфиденциальности AgroLlama: какие данные мы собираем, как используем и защищаем персональную информацию пользователей.',
    },
    terms: {
      title: 'Условия использования — AgroLlama',
      description:
        'Условия использования агромаркетплейса AgroLlama для покупателей, продавцов, физических лиц и компаний.',
    },
    support: {
      title: 'Поддержка AgroLlama',
      description:
        'Служба поддержки AgroLlama: помощь с аккаунтом, платежами, магазинами, заказами и разрешением споров.',
    },
    deletion: {
      title: 'Удаление аккаунта и данных — AgroLlama',
      description:
        'Как удалить свой аккаунт AgroLlama и связанные персональные данные: пошаговая инструкция, что удаляется, что сохраняется и сроки обработки запроса.',
    },
    notFound: {
      title: 'Страница не найдена — AgroLlama',
      description: 'Запрошенная страница AgroLlama не найдена.',
    },
  },
  kz: {
    home: {
      title: 'AgroLlama — фермерлер мен сатып алушыларға арналған агромаркетплейс',
      description:
        'AgroLlama — фермерлер мен сатып алушыларға арналған агромаркетплейс: B2B мәмілелер, ауыл шаруашылығы өнімдерін, астықты, техниканы және тыңайтқыштарды AI көмекшілерімен сатып алу және сату.',
    },
    privacy: {
      title: 'Құпиялық саясаты — AgroLlama',
      description:
        'AgroLlama құпиялық саясаты: пайдаланушы деректерін қалай жинаймыз, пайдаланамыз және қорғаймыз.',
    },
    terms: {
      title: 'Пайдалану шарттары — AgroLlama',
      description:
        'Сатып алушыларға, сатушыларға, жеке тұлғаларға және компанияларға арналған AgroLlama пайдалану шарттары.',
    },
    support: {
      title: 'AgroLlama қолдау қызметі',
      description:
        'AgroLlama қолдау қызметі: аккаунт, төлемдер, дүкендер, тапсырыстар және даулар бойынша көмек.',
    },
    deletion: {
      title: 'Аккаунт пен деректерді жою — AgroLlama',
      description:
        'AgroLlama аккаунтыңызды және байланысты жеке деректерді қалай жоюға болады: қадамдық нұсқаулық, не жойылады, не сақталады және сұранысты өңдеу мерзімдері.',
    },
    notFound: {
      title: 'Бет табылмады — AgroLlama',
      description: 'Сұралған AgroLlama беті табылмады.',
    },
  },
  en: {
    home: {
      title: 'AgroLlama — agricultural marketplace for farmers and buyers',
      description:
        'AgroLlama is an agricultural marketplace for farmers and buyers: B2B deals, produce, grain, machinery, and fertilizers, with AI assistants.',
    },
    privacy: {
      title: 'Privacy Policy — AgroLlama',
      description:
        'AgroLlama Privacy Policy: how we collect, use, and protect personal information from marketplace users.',
    },
    terms: {
      title: 'Terms of Service — AgroLlama',
      description:
        'AgroLlama marketplace terms for buyers, sellers, individuals, and businesses.',
    },
    support: {
      title: 'AgroLlama Support',
      description:
        'Get AgroLlama support for accounts, payments, stores, orders, and dispute resolution.',
    },
    deletion: {
      title: 'Account & Data Deletion — AgroLlama',
      description:
        'How to delete your AgroLlama account and associated personal data: step-by-step instructions, what is deleted, what is retained, and processing timelines.',
    },
    notFound: {
      title: 'Page not found — AgroLlama',
      description: 'The requested AgroLlama page could not be found.',
    },
  },
};

export function buildPath(language, page) {
  const languageConfig = LANGUAGES[language] || LANGUAGES.en;
  const pagePath = PAGE_PATHS[page] ?? '';

  if (page === 'home') {
    return language === 'en' ? '/' : `${languageConfig.prefix}/`;
  }

  return `${languageConfig.prefix}${pagePath}`;
}

export function getRouteInfo(pathname = '/') {
  const normalizedPath = pathname.split('?')[0].split('#')[0] || '/';
  let language = 'en';
  let pagePath = normalizedPath;

  if (normalizedPath === '/kz' || normalizedPath.startsWith('/kz/')) {
    language = 'kz';
    pagePath = normalizedPath.slice(3) || '/';
  } else if (normalizedPath === '/ru' || normalizedPath.startsWith('/ru/')) {
    language = 'ru';
    pagePath = normalizedPath.slice(3) || '/';
  }

  const comparablePath =
    pagePath.length > 1 && pagePath.endsWith('/') ? pagePath.slice(0, -1) : pagePath;
  const page =
    Object.entries(PAGE_PATHS).find(([, path]) => (path || '/') === comparablePath)?.[0] ||
    'notFound';

  return {
    language,
    page,
    isKnown: page !== 'notFound',
  };
}

export function getSeoData(pathname = '/') {
  const route = getRouteInfo(pathname);
  const copy = SEO_COPY[route.language][route.page] || SEO_COPY[route.language].notFound;
  const canonicalPath = route.isKnown
    ? buildPath(route.language, route.page)
    : pathname;
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;
  // Only English (the default, prefixless) pages are indexed. The ru/kz pages
  // stay available to users but are marked noindex, so no cross-language
  // hreflang cluster is emitted (hreflang to noindex pages is ignored anyway).
  const isIndexable = route.isKnown && route.language === 'en';
  const alternates = [];

  const organization = {
    '@type': 'Organization',
    '@id': `${SITE_ORIGIN}/#organization`,
    name: 'AgroLlama',
    legalName: 'AGROLLAMA LIMITED',
    alternateName: BRAND_ALTERNATE_NAMES,
    sameAs: BRAND_SAME_AS,
    url: `${SITE_ORIGIN}/`,
    email: 'admin@agrollama.com',
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_ORIGIN}/#logo`,
      contentUrl: BRAND_ASSETS.organizationLogo,
      url: BRAND_ASSETS.organizationLogo,
      width: 1254,
      height: 1254,
      caption: 'AgroLlama',
    },
  };

  const website = {
    '@type': 'WebSite',
    '@id': `${SITE_ORIGIN}/#website`,
    url: `${SITE_ORIGIN}/`,
    name: 'AgroLlama',
    alternateName: BRAND_ALTERNATE_NAMES,
    publisher: {
      '@id': `${SITE_ORIGIN}/#organization`,
    },
    inLanguage: ['en', 'ru', 'kk'],
  };

  const webPage = {
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: copy.title,
    description: copy.description,
    isPartOf: {
      '@id': `${SITE_ORIGIN}/#website`,
    },
    inLanguage: LANGUAGES[route.language].htmlLang,
  };

  return {
    ...route,
    ...copy,
    canonical,
    alternates,
    htmlLang: LANGUAGES[route.language].htmlLang,
    ogLocale: LANGUAGES[route.language].ogLocale,
    robots: isIndexable ? 'index,follow,max-image-preview:large' : 'noindex,follow',
    image: BRAND_ASSETS.socialImage,
    imageWidth: 1254,
    imageHeight: 1254,
    imageAlt: 'AgroLlama logo on a green background',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [organization, website, webPage],
    },
  };
}
